/**
 * Suivi GPS d'un trajet.
 *
 * ══════════════════════════════════════════════════════════════════════════
 * POURQUOI CE FICHIER A ÉTÉ RÉÉCRIT
 *
 * L'état du trajet en cours vivait dans les `useState` du hook, donc dans le
 * composant de l'écran Trajets. Quitter l'écran démontait le composant :
 * l'état disparaissait, le trajet semblait n'avoir jamais démarré, et la
 * surveillance GPS continuait de tourner sans que personne ne l'écoute — une
 * fuite qui vide la batterie en silence.
 *
 * Le pointage, lui, fonctionnait : il est écrit en base au moment du clic.
 * Le trajet fait désormais pareil.
 *
 * Le trajet en cours vit désormais dans un MAGASIN DE MODULE — une seule
 * instance pour toute l'application, comme `auth.service` — sauvegardé dans
 * localStorage. Trois conséquences :
 *
 *   · changer d'écran ne perd plus rien ; la surveillance GPS appartient au
 *     magasin, pas au composant, et n'est arrêtée que par « Arrêter » ;
 *   · recharger la page ou revenir dans l'app reprend le trajet là où il en
 *     était, distance comprise ;
 *   · la durée écoulée est CALCULÉE depuis l'heure de départ, jamais
 *     accumulée par un compteur — un compteur repartait de zéro à chaque
 *     remontage, et se décalait dès que l'onglet passait en arrière-plan.
 *
 * Depuis la migration 26, le trajet est AUSSI un brouillon en base
 * (`trip_sessions`) : perdre l'appareil ne perd plus le trajet, il est relu
 * à la connexion suivante, ici ou ailleurs. Le serveur ne recalcule jamais la
 * distance — seul l'appareil reçoit les points GPS — il en conserve le
 * compteur pour pouvoir le rendre.
 * ══════════════════════════════════════════════════════════════════════════
 */
import { useCallback, useEffect, useState, useSyncExternalStore } from 'react';
import { Trip } from '@/types/trip';
import { detectCountry } from '@/utils/indemnity';
import { tripSessionService } from '@/services/tripSession.service';
import {
  checkGeoPermission,
  requestGeoPermission,
  getCurrentPosition,
  watchPosition,
  WatchHandle,
  GeoPosition,
} from '@/services/geolocation';

export interface Position {
  lat: number;
  lng: number;
}

export type PermissionState = 'granted' | 'denied' | 'prompt' | 'checking';

const haversine = (p1: Position, p2: Position): number => {
  const R = 6371;
  const dLat = ((p2.lat - p1.lat) * Math.PI) / 180;
  const dLon = ((p2.lng - p1.lng) * Math.PI) / 180;
  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos((p1.lat * Math.PI) / 180) *
      Math.cos((p2.lat * Math.PI) / 180) *
      Math.sin(dLon / 2) *
      Math.sin(dLon / 2);
  return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
};

export const reverseGeocode = async (lat: number, lng: number): Promise<string> => {
  try {
    const res = await fetch(
      `https://nominatim.openstreetmap.org/reverse?format=json&lat=${lat}&lon=${lng}&accept-language=fr`,
      { headers: { 'Accept-Language': 'fr' } }
    );
    const data = await res.json();
    const { road, house_number, postcode, city, town, village } = data.address || {};
    const street = [house_number, road].filter(Boolean).join(' ');
    const locality = city || town || village || '';
    return [street, postcode, locality].filter(Boolean).join(', ') || data.display_name || 'Adresse inconnue';
  } catch {
    return `${lat.toFixed(5)}, ${lng.toFixed(5)}`;
  }
};

export interface TrackingState {
  isTracking: boolean;
  tripType: 'professionnel' | 'privé';
  /**
   * Motif du déplacement, saisi au démarrage.
   *
   * Il vit ici et non dans l'écran : sinon, quitter l'écran Trajets pendant
   * la course perdait ce que le conducteur venait de saisir, et l'arrêt
   * depuis l'accueil n'avait aucun motif à enregistrer.
   */
  reason: string;
  isOvertime: boolean;
  distance: number;
  elapsed: number;
  startAddress: string;
  startTime: Date | null;
  startPos: Position | null;
  currentPos: Position | null;
  locationPermission: PermissionState;
  gpsAccuracy: number | null;
  gpsSpeedKmh: number;
  isGeocodingEnd: boolean;
  pendingEndPos: Position | null;
  pendingEndAddress: string;
  showMotifModal: boolean;
  showOdometerModal: boolean;
  backgroundDenied: boolean;
}

export interface UseTrackingReturn extends TrackingState {
  setTripType: (t: 'professionnel' | 'privé') => void;
  setReason: (r: string) => void;
  setIsOvertime: (v: boolean | ((prev: boolean) => boolean)) => void;
  setLocationPermission: (p: PermissionState) => void;
  setCurrentPos: (p: Position | null) => void;
  setGpsAccuracy: (a: number | null) => void;
  setShowMotifModal: (v: boolean) => void;
  setShowOdometerModal: (v: boolean) => void;
  doStartTracking: (type: 'professionnel' | 'privé', reason?: string) => void;
  resumeTracking: () => Promise<void>;
  handleStopClick: () => Promise<void>;
  finalizeTrip: (motif: string, endAddress: string, endPos: Position | null, odometerStart?: number, odometerEnd?: number) => Trip;
  requestPermission: (showToast: (msg: string) => void) => void;
  checkPermission: () => void;
  isTrackingRef: { current: boolean };
  distanceRef: { current: number };
}

// ── Magasin de module ───────────────────────────────────────────────────────

/** Ce qui est sauvegardé. Le reste est volatil par nature (modales, GPS). */
interface Persisted {
  isTracking: boolean;
  tripType: 'professionnel' | 'privé';
  reason: string;
  isOvertime: boolean;
  distance: number;
  startAddress: string;
  startTimeMs: number | null;
  startPos: Position | null;
  lastPos: Position | null;
}

const KEY = 'autolink.trip_in_progress';

type Store = TrackingState & { startTimeMs: number | null; lastPos: Position | null };

const initial: Store = {
  isTracking: false,
  tripType: 'professionnel',
  reason: '',
  isOvertime: false,
  distance: 0,
  elapsed: 0,
  startAddress: '',
  startTime: null,
  startTimeMs: null,
  startPos: null,
  lastPos: null,
  currentPos: null,
  locationPermission: 'checking',
  gpsAccuracy: null,
  gpsSpeedKmh: 0,
  isGeocodingEnd: false,
  pendingEndPos: null,
  pendingEndAddress: '',
  showMotifModal: false,
  showOdometerModal: false,
  backgroundDenied: false,
};

function restore(): Store {
  try {
    const raw = localStorage.getItem(KEY);
    if (!raw) return initial;
    const p = JSON.parse(raw) as Persisted;
    if (!p?.isTracking) return initial;
    return {
      ...initial,
      isTracking: true,
      tripType: p.tripType ?? 'professionnel',
      reason: p.reason ?? '',
      isOvertime: !!p.isOvertime,
      distance: Number(p.distance) || 0,
      startAddress: p.startAddress ?? '',
      startTimeMs: p.startTimeMs ?? null,
      startTime: p.startTimeMs ? new Date(p.startTimeMs) : null,
      startPos: p.startPos ?? null,
      lastPos: p.lastPos ?? p.startPos ?? null,
      currentPos: p.lastPos ?? p.startPos ?? null,
    };
  } catch {
    return initial;
  }
}

let state: Store = restore();
const subscribers = new Set<() => void>();
let watchHandle: WatchHandle | null = null;

function persist(): void {
  try {
    if (!state.isTracking) { localStorage.removeItem(KEY); return; }
    const p: Persisted = {
      isTracking: true,
      tripType: state.tripType,
      reason: state.reason,
      isOvertime: state.isOvertime,
      distance: state.distance,
      startAddress: state.startAddress,
      startTimeMs: state.startTimeMs,
      startPos: state.startPos,
      lastPos: state.lastPos,
    };
    localStorage.setItem(KEY, JSON.stringify(p));
  } catch {
    /* mode privé : le trajet reste en mémoire */
  }
}

function set(patch: Partial<Store>): void {
  state = { ...state, ...patch };
  persist();
  subscribers.forEach((fn) => fn());
}

const store = {
  getState: () => state,
  subscribe(fn: () => void) {
    subscribers.add(fn);
    return () => subscribers.delete(fn);
  },
};

// Les deux « refs » attendues par l'écran Trajets. Ce sont des objets de
// module : contrairement à un useRef, ils ne meurent pas avec le composant.
const isTrackingRef = {
  get current() { return state.isTracking; },
  set current(_v: boolean) { /* piloté par le magasin */ },
} as { current: boolean };

const distanceRef = {
  get current() { return state.distance; },
  set current(v: number) { if (v === 0) set({ distance: 0 }); },
} as { current: number };

// ── Surveillance GPS ────────────────────────────────────────────────────────
// Elle appartient au magasin. Aucun démontage de composant ne l'arrête : seul
// « Arrêter le trajet » le fait.

async function startWatch(): Promise<void> {
  if (watchHandle) return;

  watchHandle = await watchPosition(
    (wp: GeoPosition) => {
      const newPos: Position = { lat: wp.lat, lng: wp.lng };
      const patch: Partial<Store> = { currentPos: newPos };

      if (wp.speed !== null && wp.speed >= 0) {
        patch.gpsSpeedKmh = Math.round(wp.speed * 3.6);
      }
      if (state.lastPos) {
        const d = haversine(state.lastPos, newPos);
        // Sous 10 m, c'est la dérive du GPS à l'arrêt, pas du déplacement :
        // l'accumuler ajouterait des kilomètres à un véhicule immobile.
        if (d > 0.01) {
          patch.distance = state.distance + d;
          patch.lastPos = newPos;
        }
      } else {
        patch.lastPos = newPos;
      }
      set(patch);
      maybePing();
    },
    (err: string) => {
      if (err === 'background_denied') set({ backgroundDenied: true });
      else console.error('Tracking GPS error:', err);
    },
    {
      desiredAccuracy: 5,
      distanceFilter: 10,
      stationaryRadius: 25,
      enableHighAccuracy: true,
      notificationTitle: 'iAUTOLINK',
      notificationText: 'Trajet en cours — GPS actif',
    }
  );
}

async function stopWatch(): Promise<void> {
  await watchHandle?.stop();
  watchHandle = null;
}

// Un trajet restauré depuis localStorage doit reprendre sa surveillance dès
// le chargement de l'application, sans attendre l'ouverture de l'écran.
if (state.isTracking) void startWatch();

// ── Brouillon serveur ───────────────────────────────────────────────────────

const PING_EVERY_MS = 30_000;
let lastPingAt = 0;
let sessionChecked = false;

/** Envoi espacé : un point GPS par seconde ne justifie pas un appel réseau. */
function maybePing(force = false): void {
  if (!state.isTracking) return;
  const now = Date.now();
  if (!force && now - lastPingAt < PING_EVERY_MS) return;
  lastPingAt = now;
  void tripSessionService.ping({
    distance_km: Number(state.distance.toFixed(3)),
    lat: state.lastPos?.lat,
    lng: state.lastPos?.lng,
    speed_kmh: state.gpsSpeedKmh || undefined,
    start_address: state.startAddress || undefined,
  });
}

/**
 * Reprise depuis le serveur, une fois au démarrage de l'app.
 *
 * Trois cas :
 *   · rien en local, un brouillon en base → nouvel appareil, cache vidé,
 *     navigation privée : on reprend le trajet ;
 *   · un trajet local, rien en base → démarré hors ligne : on le déclare ;
 *   · les deux → on garde la distance la PLUS GRANDE, jamais la plus
 *     récente. Un compteur kilométrique ne recule pas.
 */
async function syncFromServer(): Promise<void> {
  if (sessionChecked) return;
  sessionChecked = true;

  const s = await tripSessionService.current();

  if (!s) {
    if (state.isTracking) {
      await tripSessionService.start({
        kind: state.tripType === 'privé' ? 'prive' : 'professionnel',
        lat: state.startPos?.lat,
        lng: state.startPos?.lng,
        start_address: state.startAddress || undefined,
        reason: state.reason || undefined,
      });
      maybePing(true);
    }
    return;
  }

  const startedMs = Date.parse(String(s.started_at).replace(' ', 'T') + 'Z');
  const serverKm = Number(s.distance_km) || 0;
  const last = s.last_lat != null && s.last_lng != null ? { lat: s.last_lat, lng: s.last_lng } : null;

  if (!state.isTracking) {
    set({
      isTracking: true,
      tripType: s.kind === 'prive' ? 'privé' : 'professionnel',
      reason: s.reason ?? '',
      distance: serverKm,
      startAddress: s.start_address ?? '',
      startTimeMs: Number.isFinite(startedMs) ? startedMs : Date.now(),
      startTime: new Date(Number.isFinite(startedMs) ? startedMs : Date.now()),
      startPos: s.start_lat != null && s.start_lng != null ? { lat: s.start_lat, lng: s.start_lng } : null,
      lastPos: last,
      currentPos: last,
    });
    await startWatch();
  } else if (serverKm > state.distance) {
    set({ distance: serverKm });
  }
}

// ── Actions ─────────────────────────────────────────────────────────────────

async function doStartTracking(type: 'professionnel' | 'privé', reason = ''): Promise<void> {
  if (state.isTracking) return;
  set({ tripType: type, reason: reason || state.reason });

  // Une position imprécise au départ fausse tout le trajet : on insiste.
  let startGeoPos: GeoPosition | null = null;
  for (let attempts = 1; attempts <= 4; attempts++) {
    const pos = await getCurrentPosition({ enableHighAccuracy: true });
    if (!pos) break;
    if (pos.accuracy <= 80 || attempts === 4) { startGeoPos = pos; break; }
    await new Promise((r) => setTimeout(r, 3000));
  }
  if (!startGeoPos) return;

  const p: Position = { lat: startGeoPos.lat, lng: startGeoPos.lng };
  const now = Date.now();

  set({
    locationPermission: 'granted',
    startPos: p,
    currentPos: p,
    lastPos: p,
    distance: 0,
    gpsSpeedKmh: 0,
    startTimeMs: now,
    startTime: new Date(now),
    isTracking: true,
  });

  await startWatch();

  // Le brouillon serveur est posé tout de suite, comme le pointage. Si le
  // réseau manque, tripSessionService avale l'erreur : le trajet continue en
  // local et sera déclaré au prochain démarrage de l'app (syncFromServer).
  sessionChecked = true;
  void tripSessionService.start({
    kind: type === 'privé' ? 'prive' : 'professionnel',
    lat: p.lat,
    lng: p.lng,
    reason: state.reason || undefined,
  });

  // Le géocodage passe par le réseau : le trajet ne l'attend pas.
  const addr = await reverseGeocode(p.lat, p.lng);
  if (state.isTracking) {
    set({ startAddress: addr });
    maybePing(true);
  }
}

async function resumeTracking(): Promise<void> {
  if (!state.isTracking) set({ isTracking: true });
  if (!state.lastPos && state.startPos) set({ lastPos: state.startPos });
  await startWatch();
}

async function handleStopClick(): Promise<void> {
  await stopWatch();

  set({ isGeocodingEnd: true });
  const endPos = state.currentPos;
  const endAddress = endPos ? await reverseGeocode(endPos.lat, endPos.lng) : 'Adresse inconnue';

  set({
    isGeocodingEnd: false,
    pendingEndPos: endPos,
    pendingEndAddress: endAddress,
    showMotifModal: state.tripType === 'professionnel',
    showOdometerModal: state.tripType !== 'professionnel',
  });
}

function finalizeTrip(
  motif: string,
  endAddress: string,
  endPos: Position | null,
  odometerStart?: number,
  odometerEnd?: number
): Trip {
  const now = new Date();
  const start = state.startTime;
  const chosen = motif.trim() || state.reason.trim();
  const trip: Trip = {
    id: `trip-${Date.now()}`,
    type: state.tripType,
    date: now.toISOString().split('T')[0],
    startTime: start ? start.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' }) : '--:--',
    endTime: now.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' }),
    distance: parseFloat(state.distance.toFixed(2)),
    startAddress: state.startAddress,
    endAddress,
    isOvertime: state.isOvertime,
    motif: chosen || undefined,
    country: detectCountry(state.startAddress, endAddress),
    startLat: state.startPos?.lat,
    startLng: state.startPos?.lng,
    endLat: endPos?.lat,
    endLng: endPos?.lng,
    odometerStart,
    odometerEnd,
  };

  // ⚠ Le brouillon serveur N'EST PAS fermé ici.
  //
  // Il l'était, et c'était un défaut grave : `trips.create` peut refuser le
  // trajet (distance nulle, motif manquant — deux contraintes CHECK de la
  // table). Le brouillon disparaissait alors que rien n'avait été écrit dans
  // `trips` : le trajet était perdu, silencieusement.
  //
  // C'est l'appelant qui ferme, APRÈS confirmation de l'enregistrement, via
  // closeServerDraft(). Tant que le trajet n'existe pas en base, sa seule
  // trace doit survivre.
  lastPingAt = 0;
  sessionChecked = true;

  set({
    ...initial,
    locationPermission: state.locationPermission,
    currentPos: state.currentPos,
    gpsAccuracy: state.gpsAccuracy,
  });

  return trip;
}

/**
 * Ce que l'on enregistrerait si on arrêtait maintenant, SANS rien effacer.
 *
 * Séparé de finalizeTrip parce que l'ordre compte : on lit, on enregistre,
 * et on n'efface qu'une fois l'enregistrement confirmé.
 */
export function snapshotTrip(endAddress: string, endPos: Position | null) {
  const startedMs = state.startTimeMs ?? Date.now();
  const sql = (ms: number) => new Date(ms).toISOString().slice(0, 19).replace('T', ' ');
  return {
    kind: (state.tripType === 'privé' ? 'prive' : 'professionnel') as 'prive' | 'professionnel',
    source: 'gps' as const,
    distance_km: Number(state.distance.toFixed(3)),
    duration_seconds: Math.max(0, Math.round((Date.now() - startedMs) / 1000)),
    started_at: sql(startedMs),
    ended_at: sql(Date.now()),
    // `undefined` et non `null` : les champs facultatifs de NewTrip sont
    // typés optionnels, et l'API traite l'absence comme un null.
    start_address: state.startAddress || undefined,
    end_address: endAddress || undefined,
    start_lat: state.startPos?.lat,
    start_lng: state.startPos?.lng,
    end_lat: endPos?.lat,
    end_lng: endPos?.lng,
    reason: state.reason.trim() || undefined,
  };
}

/** Ferme le brouillon serveur. À n'appeler qu'une fois le trajet en base. */
export async function closeServerDraft(cancelled = false): Promise<void> {
  await tripSessionService.end(cancelled);
}

/** Remet le suivi à zéro après un enregistrement réussi (ou une annulation). */
export function clearTrip(): void {
  lastPingAt = 0;
  sessionChecked = true;
  set({
    ...initial,
    locationPermission: state.locationPermission,
    currentPos: state.currentPos,
    gpsAccuracy: state.gpsAccuracy,
  });
}

async function checkPermission(): Promise<void> {
  set({ locationPermission: 'checking' });
  const status = await checkGeoPermission();
  if (status === 'granted') {
    set({ locationPermission: 'granted' });
    const pos = await getCurrentPosition({ enableHighAccuracy: false });
    // Ne pas écraser la position d'un trajet en cours avec une lecture
    // approximative : elle ajouterait des kilomètres fantômes.
    if (pos && !state.isTracking) set({ currentPos: { lat: pos.lat, lng: pos.lng }, gpsAccuracy: pos.accuracy });
    else if (pos) set({ gpsAccuracy: pos.accuracy });
  } else {
    set({ locationPermission: status === 'denied' ? 'denied' : 'prompt' });
  }
}

async function requestPermission(showToast: (msg: string) => void): Promise<void> {
  const status = await requestGeoPermission();
  if (status === 'granted') {
    set({ locationPermission: 'granted' });
    const pos = await getCurrentPosition({ enableHighAccuracy: false });
    if (pos && !state.isTracking) set({ currentPos: { lat: pos.lat, lng: pos.lng } });
  } else if (status === 'background_denied') {
    set({ locationPermission: 'granted', backgroundDenied: true });
    showToast('GPS de premier plan accordé. Le suivi en arrière-plan nécessite la permission « Toujours ».');
  } else {
    set({ locationPermission: 'denied' });
    showToast('Géolocalisation refusée. Activez-la dans Réglages.');
  }
}

// ── Le hook ─────────────────────────────────────────────────────────────────

/**
 * Un trajet est-il en cours ? Lisible hors React.
 *
 * Utile pour l'état initial d'un écran : au premier rendu, on doit déjà
 * savoir s'il faut afficher le suivi plutôt que la liste — attendre un effet
 * ferait clignoter la liste par-dessus un trajet en cours.
 */
export function isTripRunning(): boolean {
  return state.isTracking;
}

export function useTracking(): UseTrackingReturn {
  const s = useSyncExternalStore(store.subscribe, store.getState, store.getState);

  // Durée écoulée : calculée depuis l'heure de départ, jamais accumulée.
  // Un compteur qui s'incrémente repart de zéro à chaque remontage et se
  // décale dès que l'onglet passe en arrière-plan.
  const [, tick] = useState(0);
  useEffect(() => {
    if (!s.isTracking) return;
    const t = setInterval(() => tick((n) => n + 1), 1000);
    return () => clearInterval(t);
  }, [s.isTracking]);
  const elapsed = s.startTimeMs ? Math.floor((Date.now() - s.startTimeMs) / 1000) : 0;

  // La vérification de permission ne part qu'une fois par montage d'écran,
  // et jamais pendant un trajet — elle n'a rien à y corriger.
  useEffect(() => { if (!store.getState().isTracking) void checkPermission(); }, []);

  // Reprise du brouillon serveur : une seule fois pour toute la session,
  // `syncFromServer` porte son propre verrou.
  useEffect(() => { void syncFromServer(); }, []);

  // Dernier signe de vie avant que l'onglet ne parte en arrière-plan : sur
  // mobile, l'app peut être tuée sans autre avertissement.
  useEffect(() => {
    const onHide = () => { if (document.visibilityState === 'hidden') maybePing(true); };
    document.addEventListener('visibilitychange', onHide);
    return () => document.removeEventListener('visibilitychange', onHide);
  }, []);

  const setIsOvertime = useCallback((v: boolean | ((prev: boolean) => boolean)) => {
    set({ isOvertime: typeof v === 'function' ? v(store.getState().isOvertime) : v });
  }, []);

  return {
    ...s,
    elapsed,
    setTripType: useCallback((t: 'professionnel' | 'privé') => set({ tripType: t }), []),
    setReason: useCallback((r: string) => set({ reason: r }), []),
    setIsOvertime,
    setLocationPermission: useCallback((p: PermissionState) => set({ locationPermission: p }), []),
    setCurrentPos: useCallback((p: Position | null) => set({ currentPos: p }), []),
    setGpsAccuracy: useCallback((a: number | null) => set({ gpsAccuracy: a }), []),
    setShowMotifModal: useCallback((v: boolean) => set({ showMotifModal: v }), []),
    setShowOdometerModal: useCallback((v: boolean) => set({ showOdometerModal: v }), []),
    doStartTracking,
    resumeTracking,
    handleStopClick,
    finalizeTrip,
    requestPermission,
    checkPermission,
    isTrackingRef,
    distanceRef,
  };
}
